import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Wrench } from 'lucide-react';
import SEO, { type Crumb } from '@/components/SEO';
import PageHeader from '@/components/PageHeader';
import ContentIndex from '@/components/ContentIndex';
import CTABand from '@/components/CTABand';
import FragmentLink from '@/components/FragmentLink';
import { allContent, staticPage } from '@/lib/content';
import { tools } from '@/tools/registry';

// Start with the invoice-process overview, then retain the library's date order.
const guides = [
  ...allContent.filter((item) => item.path === '/notes/what-is-fipsa'),
  ...allContent.filter((item) => item.path !== '/notes/what-is-fipsa'),
];

const Resources = () => {
  const page = staticPage('/resources');
  const crumbs: Crumb[] = [{ name: 'Home', href: '/' }, { name: 'Resources', href: '/resources' }];

  return (
    <>
      <SEO title={page.title} description={page.description} path="/resources" breadcrumbs={crumbs} />
      <PageHeader
        crumbs={crumbs}
        label="Resources"
        title="Find your next step."
        lead="Practical tools and guides for the WebCenter estate you have. Explore a decision, trace an invoice-process problem, or find the detail behind an upgrade or migration."
      >
        <FragmentLink hash="#guides-heading" className="mt-7 inline-flex items-center gap-3 font-bold underline underline-offset-4">
          Browse all {allContent.length} guides <ArrowRight className="h-5 w-5" aria-hidden="true" />
        </FragmentLink>
      </PageHeader>

      <section className="container py-12 md:py-16" aria-labelledby="resource-start-heading">
        <h2 id="resource-start-heading" className="sr-only">Choose a starting point</h2>
        <div className="grid gap-6 md:grid-cols-2">
          <article className="flex flex-col border border-rule bg-cream-light p-7 md:p-9">
            <div className="mb-7 flex items-center justify-between gap-4">
              <p className="label">Work through a decision</p>
              <Wrench className="h-7 w-7 shrink-0" aria-hidden="true" />
            </div>
            <h3 className="text-subhead">Free tools</h3>
            <p className="mt-4 mb-7 text-body">Compare options, check support dates and organize the information you already have. {tools.length} browser-based tools, with assumptions and limitations explained.</p>
            <Link to="/tools" className="mt-auto inline-flex items-center gap-3 self-start font-bold underline underline-offset-4">
              Explore free tools <ArrowRight className="h-5 w-5" aria-hidden="true" />
            </Link>
          </article>
          <article className="flex flex-col border border-rule bg-cream-light p-7 md:p-9">
            <div className="mb-7 flex items-center justify-between gap-4">
              <p className="label">Understand the moving parts</p>
              <BookOpen className="h-7 w-7 shrink-0" aria-hidden="true" />
            </div>
            <h3 className="text-subhead">Field notes</h3>
            <p className="mt-4 mb-7 text-body">Explore invoice recognition, workflow, document access and ERP connections. Read the scope and sources behind the guidance before applying it to your environment.</p>
            <Link to="/notes" className="mt-auto inline-flex items-center gap-3 self-start font-bold underline underline-offset-4">
              Read field notes <ArrowRight className="h-5 w-5" aria-hidden="true" />
            </Link>
          </article>
        </div>
      </section>

      <section className="container border-t border-rule py-12 md:py-16" aria-labelledby="guides-heading">
        <h2 id="guides-heading" className="scroll-mt-24 text-subhead">Browse every guide</h2>
        <p className="mt-4 mb-10 max-w-3xl text-body">Find upgrade, migration and support guidance by component, task or topic. Each article explains its scope, supporting sources and what still needs to be verified in your estate.</p>
        <ContentIndex items={guides} />
      </section>
      <CTABand variant="engineer" />
    </>
  );
};

export default Resources;
